import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createTestSession,
  fetchTestSessions,
  deleteSession,
  cloneSession,
  fetchSubscribersWithSessions,
  approveSession,
  fetchSessionStep2Selections,
  getSessionRunResults,
  DowloadTestSessionMaterials,
} from "../api";
import {
  Step2VM,
  TestSessionDto,
  TestSessionsListVM,
  TestSessionStatusDto,
  UserDto,
  ValimateExecutionSummaryVM,
} from "../../autogenerated/models";
import { toast } from "react-toastify";
import { ApplicationState } from "..";

export interface TestSessionsState {
  isLoading: boolean;
  users: UserDto[];
  usersPage: number;
  sessionSummaryModalChoices: Step2VM;
  sessionSummaryModalUser: UserDto;
  sessionSummaryModalSession: TestSessionDto;
  sessionSummaryModalResults: ValimateExecutionSummaryVM;
  isSummaryModalLoading: boolean;
  testSessions: TestSessionsListVM["testSessions"];
  statuses: TestSessionsListVM["testSessionStatuses"];
}

const initialState: TestSessionsState = {
  isLoading: false,
  isSummaryModalLoading: false,
  testSessions: [],
  statuses: [],
  users: [],
  sessionSummaryModalChoices: {},
  sessionSummaryModalSession: {},
  sessionSummaryModalUser: {},
  sessionSummaryModalResults: {},
  usersPage: 0,
};

export const testSessionsSlice = createSlice({
  name: "testSessions",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setUsersPage: (state, action) => {
      state.usersPage = action.payload;
    },
    setSessionSummaryModalChoices: (state, action) => {
      state.sessionSummaryModalChoices = action.payload;
    },
    setSessionSummaryModalSession: (state, action) => {
      state.sessionSummaryModalSession = action.payload;
    },
    setSessionSummaryModalResults: (
      state,
      action: PayloadAction<ValimateExecutionSummaryVM>
    ) => {
      state.sessionSummaryModalResults = action.payload;
    },
    setSessionSummaryModalUser: (state, action) => {
      state.sessionSummaryModalUser = action.payload;
    },
    setIsSummaryModalLoading: (state, action: PayloadAction<boolean>) => {
      state.isSummaryModalLoading = action.payload;
    },
    setTestSessionsList: (state, action: PayloadAction<TestSessionsListVM>) => {
      state.testSessions = [...action.payload.testSessions!];
      state.statuses = action.payload.testSessionStatuses;
    },
    setTestSessionStatus: (
      state,
      action: PayloadAction<{
        userId: string;
        sessionId: string;
        status: TestSessionStatusDto;
      }>
    ) => {
      let userIndex = state.users.findIndex(
        (u) => u.id === action.payload.userId
      );
      let sessionIndex =
        state.users[userIndex].testSessions?.findIndex(
          (ts) => ts.id === action.payload.sessionId
        ) ?? 0;
      (state.users[userIndex].testSessions ?? [])[sessionIndex].status =
        action.payload.status;
    },
  },
});

export const { actions } = testSessionsSlice;

export const getTestSessions = () => (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  return fetchTestSessions()
    .then((sessions) => {
      dispatch(actions.setTestSessionsList(sessions));
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => dispatch(actions.setIsLoading(false)));
};

export const addTestSession = () => (dispatch: any) =>
  createTestSession()
    .then((createdNewSessionId: string) => {
      return createdNewSessionId;
    })
    .catch((error: any) => {
      console.log(error);
    });

export const removeSession = (sessionId: string) => (dispatch: any) => {
  dispatch(actions.setIsLoading(true));
  deleteSession(sessionId)
    .then(() => {
      toast.success("Session deleted.");
      dispatch(getTestSessions());
    })
    .catch((error: any) => {
      // restClient already showed the server's reason
      console.log(error);
    })
    .finally(() => dispatch(actions.setIsLoading(false)));
};

export const duplicateSession =
  (sessionId: string, withFiles: boolean) => (dispatch: any) => {
    dispatch(actions.setIsLoading(true));
    cloneSession(sessionId, withFiles)
      .then(() => {
        toast.success("Session duplicated.");
        dispatch(getTestSessions());
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => dispatch(actions.setIsLoading(false)));
  };

export const getSessionMaterials = (sessionId: string, dispatch: any) => {
  return new Promise<Blob>((resolve) => {
    dispatch(actions.setIsLoading(true));
    DowloadTestSessionMaterials(sessionId)
      .then((result: Blob) => {
        resolve(result);
      })
      .then(() => {})
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => dispatch(actions.setIsLoading(false)));
  });
};

export const getSubscribersWithSessions = (pageNumber: number) => {
  return (dispatch: any) => {
    dispatch(actions.setIsLoading(true));
    //dispatch(actions.setPage(pageNumber));
    return fetchSubscribersWithSessions(pageNumber)
      .then((users) => {
        dispatch(actions.setUsers(users));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => dispatch(actions.setIsLoading(false)));
  };
};

export const getSessionSummary =
  (sessionId: string, userId: string) =>
  (dispatch: any, getState: () => ApplicationState) => {
    dispatch(actions.setIsSummaryModalLoading(true));
    let user = getState().testSessions.users.find((user) => user.id === userId);
    let session = user?.testSessions?.find(
      (session) => session.id === sessionId
    );
    dispatch(actions.setSessionSummaryModalUser(user));
    dispatch(actions.setSessionSummaryModalSession(session));
    fetchSessionStep2Selections(sessionId)
      .then((result) => dispatch(actions.setSessionSummaryModalChoices(result)))
      .finally(() => {
        if (
          session?.status?.short === "success" ||
          session?.status?.short === "failed"
        )
          getSessionRunResults(sessionId)
            .then((results) => {
              dispatch(actions.setSessionSummaryModalResults(results));
            })
            .finally(() => dispatch(actions.setIsSummaryModalLoading(false)));
        else dispatch(actions.setIsSummaryModalLoading(false));
      });
  };

export const clearSessionSummary = () => (dispatch: any) => {
  dispatch(actions.setSessionSummaryModalChoices({}));
  dispatch(actions.setSessionSummaryModalUser({}));
  dispatch(actions.setSessionSummaryModalSession({}));
  dispatch(actions.setSessionSummaryModalResults({}));
};

export const approveTestSession =
  (userId: string, sessionId: string) => (dispatch: any) => {
    dispatch(actions.setIsLoading(true));
    approveSession(sessionId)
      .then((result) =>
        dispatch(
          actions.setTestSessionStatus({
            userId: userId,
            sessionId: sessionId,
            status: result.session?.status ?? {},
          })
        )
      )
      .finally(() => dispatch(actions.setIsLoading(false)));
  };
